import { UserFacingError } from '../errors.js';

// In-memory activity is deliberately not persisted: after a process crash an
// unfinished intent must become recoverable again.
const activePrintIntents = new Set<string>();

export function isLabelPrintIntentActive(intentId: string): boolean {
  return activePrintIntents.has(intentId);
}

export async function withActiveLabelPrintIntent<T>(
  intentId: string,
  work: () => Promise<T>,
): Promise<T> {
  if (activePrintIntents.has(intentId)) throw new Error('label_print_already_running');
  activePrintIntents.add(intentId);
  try {
    return await work();
  } finally {
    activePrintIntents.delete(intentId);
  }
}

export class DurableLabelPrintExecutionError extends UserFacingError {
  constructor(
    message: string,
    readonly physicalPrintCompleted: boolean,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'DurableLabelPrintExecutionError';
  }
}

export interface DurableLabelPrintFlow {
  /** Persists the StockCommand and its print-intent master in one transaction. */
  persistCommandAndIntent: () => Promise<void>;
  printPhysicalLabel: () => Promise<void>;
  /** Persists the physical outcome; network delivery remains background work. */
  finalizePrinted: () => Promise<void>;
  finalizeFailed: (message: string) => Promise<void>;
  describeError: (error: unknown) => string;
}

/**
 * Offline-first physical boundary. The printer never starts before the
 * compound is durable and never waits for a remote ACK. The sync engine holds
 * label commands until finalizePrinted/finalizeFailed has persisted an outcome.
 */
export async function runDurableLabelPrintFlow(
  flow: DurableLabelPrintFlow,
): Promise<void> {
  await flow.persistCommandAndIntent();

  try {
    await flow.printPhysicalLabel();
  } catch (error) {
    const message = flow.describeError(error);
    try {
      await flow.finalizeFailed(message);
    } catch {
      // The pending intent is the restart-safe recovery marker. Do not invent
      // a printed outcome when the printer did not report success.
    }
    throw new DurableLabelPrintExecutionError(message, false, error);
  }

  try {
    await flow.finalizePrinted();
  } catch (error) {
    throw new DurableLabelPrintExecutionError(
      `Stampa completata, ma registrazione dell'esito non confermata: ${
        flow.describeError(error)
      }. Non ristampare; l'intento è salvato sul dispositivo.`,
      true,
      error,
    );
  }
}
